// --- Our Modules --------------------------------------------------------------------------------
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type TyBarPoint = {
    label: string;
    /** Every request in the bucket. Drawn as the full bar. */
    total: number;
    /** Genuine breakage — 5xx. Stacked at the foot of the bar, in red. */
    errors?: number;
    /** Expected refusals — expired tokens, permissions, 404s. Stacked above the errors, in amber. */
    rejected?: number;
};

type Props = {
    points: TyBarPoint[];
    emptyLabel: string;
    describe?: (point: TyBarPoint) => string;
};

// --- Logic --------------------------------------------------------------------------------------

const HEIGHT = 120;
const SLOT = 10;
const GAP = 2;

/**
 * Traffic over time, with errors and rejections stacked inside each bar rather than beside it — the
 * question is "how much of that broke", and adjacent bars make that a subtraction. Errors sit at the
 * foot so the red always starts from the same baseline and can be compared across buckets by eye.
 *
 * Hand-drawn SVG on purpose: a chart library would be the first MUI in the layout layer, and this
 * needs three rectangles, not a plotting engine.
 */
export default function BarChart({ points, emptyLabel, describe }: Props) {
    if (!points.length) return <p className="bar-chart-empty">{emptyLabel}</p>;

    const peak = Math.max(...points.map((point) => point.total), 1);
    const width = points.length * SLOT;
    const barWidth = Math.max(1, SLOT - GAP);

    return (
        // The time axis reads left-to-right in both languages — a mirrored chart would put the most
        // recent bar where an Arabic reader expects the oldest.
        <div className="bar-chart" dir="ltr">
            <svg className="bar-chart-svg" viewBox={`0 0 ${width} ${HEIGHT}`} preserveAspectRatio="none" role="img">
                {points.map((point, index) => {
                    const scale = (value: number) => (value / peak) * HEIGHT;
                    const totalH = scale(point.total);
                    const errorH = scale(point.errors ?? 0);
                    const rejectedH = scale(point.rejected ?? 0);
                    const x = index * SLOT;

                    return (
                        <g key={point.label}>
                            <rect x={x} y={HEIGHT - totalH} width={barWidth} height={totalH} className="bar-chart-bar" />
                            {rejectedH > 0 && (
                                <rect
                                    x={x}
                                    y={HEIGHT - errorH - rejectedH}
                                    width={barWidth}
                                    height={rejectedH}
                                    className="bar-chart-bar bar-chart-bar--rejected"
                                />
                            )}
                            {errorH > 0 && (
                                <rect x={x} y={HEIGHT - errorH} width={barWidth} height={errorH} className="bar-chart-bar bar-chart-bar--error" />
                            )}
                            <title>{describe ? describe(point) : `${point.label}: ${point.total}`}</title>
                        </g>
                    );
                })}
            </svg>

            <div className="bar-chart-axis">
                <span>{points[0].label}</span>
                {points.length > 1 && <span>{points[points.length - 1].label}</span>}
            </div>
        </div>
    );
}
